import { LoadingSpinner } from "components/LoadingSpinner";
import { AccountFeeType } from "constants/fee";
import { validationConstants } from "constants/formValidation";
import { ToastContext } from "context/ToastContext";
import { calculateClearPatientFee } from "helpers/fee";
import {
  calculateDiscount,
  centsToDollars,
  dollarsToCents,
} from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { useGetAccountDiscountAndFee } from "hooks/useGetAccountDiscountAndFee";
import usePriceNew from "hooks/usePrice";
import { useProviderProcedure } from "hooks/useProviderProcedure";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { isEmpty, debounce } from "lodash";
import { StyledCard } from "pages/CreateOrder/CreateOrder.styled";
import { convertToUTC } from "pages/CreateOrder/utils/helpers";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
  useRef,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "store";
import { useGetAccountByIdQuery } from "store/queries/account";
import {
  useSearchProviderProcedureMutation,
  useGetCptRecommendationsMutation,
} from "store/queries/providerProcedure";
import {
  setPriceBreakdownRows,
  setRows,
  IPriceBreakdownRows,
  setAutoCompleteValue,
  setSelectedProcedures,
  addSelectedProcedures,
  setCptRecommendations,
  setLoadingCptRecommendations,
  clearCptRecommendations,
} from "store/slices/serviceDetails";
import styled from "styled-components";
import { IPatient } from "types/Patient";
import { SpecialtyList, SpecialtyListDict } from "types/Provider/Provider";

import { ProcedureRow } from "../../../../../../types/CreateOrder";
import {
  buildFilteredProceduresSuggestions,
  buildGFETotalAmount,
  buildFirstColumnWithQuantityTemplate,
  buildSelectedProceduresPriceBreakdownToTable,
  buildSelectedProceduresObject,
  buildSelectedProceduresToTable,
} from "../../builders";
import { autoCompleteFieldHandleOnSelectProcedure } from "../../handlers";
import { replicateByCptQuantity, getCptQuantities } from "../../helpers";
import { AutoCompleteCPT } from "../Common/AutoCompleteCPT";
import { CptRecommendations } from "../Common/CptRecommendations";
import { ImplantSuggestion } from "../Common/ImplantSuggestion";
import { ProviderDropdown } from "../Common/ProviderDropdown";
import { RemoveItem } from "../Common/RemoveItem";
import { AdjustmentDetails } from "./AdjustmentDetails";
import { PatientResponsibilityDetails } from "./PatientResponsibilityDetails";

export const GFEFieldsContainer = styled.div`
  display: flex;
  justify-content: end;
  margin-top: ${sizer(4)};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  margin-bottom: 30px;
`;

export const CardHeader = styled.div``;

const StyledWarning = styled(Message)`
  background: transparent !important;
  max-width: 300px;
`;

interface IProgressiveDeductible {
  totalDeductibleForMatchingOrders: number;
  totalOutOfPocketForMatchingOrders: number;
  matchingOrders?: IMatchingOrder[];
}

interface IMatchingOrder {
  id: string;
  dateOfService: string;
  amountInCents: number;
  deductibleReductionInCents: number;
  oopReductionInCents: number;
  gfeQuote: {
    pricingBreakdown: {
      procedureBreakdown: {
        owedByPatient: {
          deductible: number;
          total: number;
          selfPay: number;
        };
      }[];
    };
  };
  patient: IPatient;
  status: string;
  accountId: string;
}

export function GFEServiceDetails({
  redo = false,
  draft = false,
  copy = false,
  totalAmount,
  initialProviderProcedures,
}: {
  redo?: boolean;
  draft?: boolean;
  copy?: boolean;
  totalAmount?: number;
  initialProviderProcedures?: any;
}) {
  const query = useQuery();
  const dispatch = useDispatch();
  const { id, draftId } = useParams<{ id?: string; draftId?: string }>();
  const orderId = redo || draft ? id || draftId : undefined;

  const accountId = query.get("accountId") || undefined;

  const {
    control,
    setValue,
    watch,
    trigger,
    formState: { errors },
    getValues,
  } = useFormContext();
  const { current: toastElement } = useContext(ToastContext);
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const [searchMutation] = useSearchProviderProcedureMutation();
  const [getCptRecommendations] = useGetCptRecommendationsMutation();
  const [showCPTCodesNotFound, setShowCPTCodesNotFound] = useState(false);
  const [showCustomPriceMessage, setShowCustomPriceMessage] = useState(false);
  const [notFoundCPTCodes, setNotFoundCPTCodes] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadingCptCode, setLoadingCptCode] = useState<string | null>(null);
  const [currentProviderProcedures, setCurrentProviderProcedures] =
    useState<any>(initialProviderProcedures || []);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isCptInputFocused, setIsCptInputFocused] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const excludedNcciRulesetIds: string[] =
    watch("excludedNcciRulesetIds") || [];
  const setExcludedNcciRulesetIds = (
    updater: string[] | ((prev: string[]) => string[])
  ) => {
    const newVal =
      typeof updater === "function" ? updater(excludedNcciRulesetIds) : updater;
    setValue("excludedNcciRulesetIds", newVal);
  };
  const excludedNcciInfo: Array<{
    rulesetId: string;
    cptCode: string;
    discountedCptCode: string;
  }> = watch("excludedNcciInfo") || [];
  const setExcludedNcciInfo = (
    updater:
      | Array<{ rulesetId: string; cptCode: string; discountedCptCode: string }>
      | ((
          prev: Array<{
            rulesetId: string;
            cptCode: string;
            discountedCptCode: string;
          }>
        ) => Array<{
          rulesetId: string;
          cptCode: string;
          discountedCptCode: string;
        }>)
  ) => {
    const newVal =
      typeof updater === "function" ? updater(excludedNcciInfo) : updater;
    setValue("excludedNcciInfo", newVal);
  };

  const payerNameField: string = watch("payerName");
  const payerField = watch("payer");
  const serviceDetailsField = watch("serviceDetails");
  const deductibleField: number = watch("deductible");
  const outOfPocketMaxField: number = watch("outOfPocketMax");
  const flatCopayField: number = watch("flatCopay");
  const coinsurancePercentField: number = watch("coinsurancePercent");
  const redoOrderFirstRenderField = watch("redoOrderFirstRender");
  const draftOrderFirstRenderField = watch("draftOrderFirstRender");
  const copyOrderFirstRenderField = watch("copyOrderFirstRender");
  const facilityCopayField = watch("facilityCopay");
  const facilityCoinsuranceField = watch("facilityCoinsurance");
  const facilityDeductibleField = watch("facilityDeductible");
  const professionalFlatCopayField = watch("professionalFlatCopay");
  const showProfessionalCopayField = watch("showProfessionalCopay");
  const professionalCopayField = watch("professionalCopay");
  const professionalCoinsuranceField = watch("professionalCoinsurance");
  const professionalDeductibleField = watch("professionalDeductible");
  const dateOfServiceField = watch("dateOfService");
  const firstNameField = watch("firstName");
  const lastNameField = watch("lastName");
  const dateOfBirthField = watch("dateOfBirth");
  const reductionsAppliedField = watch("reductionsApplied");
  // secondary insurance fields
  const isSecondaryInsuranceActivatedField = watch(
    "secondaryInsuranceActivated"
  );
  const deductible2Field: number = watch("deductible2");
  const outOfPocketMax2Field: number = watch("outOfPocketMax2");
  const flatCopay2Field: number = watch("flatCopay2");
  const professionalFlatCopay2Field: number = watch("professionalFlatCopay2");
  const showProfessionalCopay2Field = watch("showProfessionalCopay2");
  const coinsurancePercent2Field: number = watch("coinsurancePercent2");
  const facilityCopay2Field = watch("facilityCopay2");
  const facilityCoinsurance2Field = watch("facilityCoinsurance2");
  const facilityDeductible2Field = watch("facilityDeductible2");
  const professionalCopay2Field = watch("professionalCopay2");
  const professionalCoinsurance2Field = watch("professionalCoinsurance2");
  const professionalDeductible2Field = watch("professionalDeductible2");
  const totalPatientEstimateField = watch("amount");
  const initialClearPatientFeeValueInPercentageField = watch(
    "initialClearPatientFeeValueInPercentage"
  );

  const {
    getGFEPrices,
    priceBreakdown,
    insuranceAmounts,
    getInsuranceAmounts,
  } = usePriceNew();

  const { accountDiscount: discount, isLoading: isDiscountLoading } =
    useGetAccountDiscountAndFee(AccountFeeType.ClearHealthDiscountForGFE);

  const { procedures, providerProcedures, search } = useProviderProcedure({
    payerNameField,
  });

  const {
    rows = [],
    autoCompleteValue = [],
    selectedProcedures = [],
    dataTableRows = [],
    displayAdjustments = false,
    displayPatientResponsibility = false,
    priceBreakdownRows = {} as IPriceBreakdownRows,
    loadingPriceBreakdown: priceExecuting,
  } = useSelector((state: RootState) => state.serviceDetailsSlice);

  // For the payer specific account fees, we need to fetch account data to get them
  const { data: accountData } = useGetAccountByIdQuery(accountId || "", {
    skip: !accountId,
  });

  // And we calculate Clear Patient Fee based on selected payer, if it exists
  // For redo/draft: ALWAYS use the order's saved value from the order table.
  //   - If the order has a fee (number > 0): use it
  //   - If the order has no fee (null or 0): use 0, do NOT fall back to account
  // For initial creation/copy: look up from account table
  const { clearPatientFee, clearPatientFeePercentage } = useMemo(() => {
    if (!totalPatientEstimateField) {
      return { clearPatientFee: 0, clearPatientFeePercentage: 0 };
    }

    // For redo/draft: always use the order's saved value, never the account
    // (order has no clearPatientFee when value is null or 0)
    if (redo || draft) {
      const percentage = initialClearPatientFeeValueInPercentageField ?? 0;
      const feeAmount =
        percentage > 0
          ? calculateClearPatientFee(totalPatientEstimateField, percentage)
          : 0;
      return {
        clearPatientFee: feeAmount,
        clearPatientFeePercentage: percentage,
      };
    }

    // For initial creation/copy: look up from account's payer fees
    if (!payerField?.payerId || !accountData?.data?.payerAccountFees) {
      return { clearPatientFee: 0, clearPatientFeePercentage: 0 };
    }
    const payerPatientFee = accountData.data.payerAccountFees.find(
      (fee) =>
        fee.payerId === payerField.payerId && fee.type === "ClearPatientFee"
    );

    if (!payerPatientFee) {
      return { clearPatientFee: 0, clearPatientFeePercentage: 0 };
    }

    // Calculate fee with rounding up to nearest $5
    const feeAmount = calculateClearPatientFee(
      totalPatientEstimateField,
      payerPatientFee.valueInPercentage
    );
    // OLD LOGIC (exact percentage):
    // const feeAmount =
    //   (totalPatientEstimateField * payerPatientFee.valueInPercentage) / 100;
    return {
      clearPatientFee: feeAmount,
      clearPatientFeePercentage: payerPatientFee.valueInPercentage,
    };
  }, [
    payerField?.payerId,
    accountData?.data?.payerAccountFees,
    totalPatientEstimateField,
    redo,
    draft,
    initialClearPatientFeeValueInPercentageField,
  ]);

  // Calculate total amount including Clear Patient Fee
  const totalAmountWithPatientFee = useMemo(() => {
    if (!totalPatientEstimateField) return 0;
    return totalPatientEstimateField + clearPatientFee;
  }, [totalPatientEstimateField, clearPatientFee]);

  const actionTemplate = (row: ProcedureRow) => {
    return <RemoveItem row={row} autoCompleteValue={autoCompleteValue} />;
  };

  const providerDropdownTemplate = (row: ProcedureRow) => {
    return (
      <ProviderDropdown
        row={row}
        currentProviderProcedures={currentProviderProcedures}
      />
    );
  };

  useEffect(() => {
    if (isEmpty(rows)) getInsuranceAmounts(undefined);
    setValue(
      "serviceDetails",
      rows.map((row: ProcedureRow) => ({
        code: row.cptCode,
        specialty: row.specialty,
        amountInCents: row.amount,
        procedureId: row.procedureId,
        cptDescription: row.description,
        providerId: row.selectedProvider.id,
        providerName: row.selectedProvider.name,
        providerProcedureId: row.providerProcedureId,
        benefitsForProvProc: row.benefitSelectionType
          ? {
              benefitSelectionType: row.benefitSelectionType,
            }
          : undefined,
      }))
    );
    // We need to set clearPatientFee in form data so it can be used to calculate total patientresponsibilityamountincents in service details
    setValue("clearPatientFee", clearPatientFee || 0);
    setValue("clearPatientFeePercentage", clearPatientFeePercentage || 0);
  }, [rows, clearPatientFee, clearPatientFeePercentage]);

  const isValidDate = (dateStr: string) => {
    if (!dateStr) return false;
    // Check if string matches MM/DD/YYYY format
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    return dateRegex.test(dateStr);
  };

  // Clear excluded NCCI state when the user manually changes procedures.
  // We track the previous procedure count to distinguish user edits from initial load.
  const prevProcCountRef = useRef<number | null>(null);
  useEffect(() => {
    const currentCount = Array.isArray(serviceDetailsField)
      ? serviceDetailsField.length
      : 0;
    if (
      prevProcCountRef.current !== null &&
      prevProcCountRef.current !== currentCount &&
      currentCount > 0 &&
      prevProcCountRef.current > 0
    ) {
      setExcludedNcciRulesetIds([]);
      setExcludedNcciInfo([]);
    }
    prevProcCountRef.current = currentCount;
  }, [serviceDetailsField]);

  useEffect(() => {
    if (isEmpty(serviceDetailsField)) {
      setPriceBreakdownRows({ adjustments: [], patientResponsibility: [] });
      return;
    }

    const costApplicationRules = {
      facilityRules: {
        useCopay: facilityCopayField,
        useCoinsurance: facilityCoinsuranceField,
        useDeductible: facilityDeductibleField,
      },
      nonFacilityRules: {
        useCopay: professionalCopayField,
        useCoinsurance: professionalCoinsuranceField,
        useDeductible: professionalDeductibleField,
      },
    };
    const costApplicationRules2 = {
      facilityRules: {
        useCopay: facilityCopay2Field,
        useCoinsurance: facilityCoinsurance2Field,
        useDeductible: facilityDeductible2Field,
      },
      nonFacilityRules: {
        useCopay: professionalCopay2Field,
        useCoinsurance: professionalCoinsurance2Field,
        useDeductible: professionalDeductible2Field,
      },
    };

    getGFEPrices({
      serviceDetails: { procedures: serviceDetailsField },
      remainingDeductibleInDollar: deductibleField,
      outOfPocketMaxInDollar: outOfPocketMaxField,
      flatCopayInDollar: flatCopayField,
      professionalFlatCopayInDollar: professionalFlatCopayField,
      showProfessionalCopay: showProfessionalCopayField,
      coinsurancePercent: coinsurancePercentField,
      costApplicationRules,
      remainingDeductible2InDollar: deductible2Field,
      outOfPocketMax2InDollar: outOfPocketMax2Field,
      flatCopay2InDollar: flatCopay2Field,
      professionalFlatCopay2InDollar: professionalFlatCopay2Field,
      showProfessionalCopay2: showProfessionalCopay2Field,
      coinsurancePercent2: coinsurancePercent2Field,
      costApplicationRules2,
      isSecondaryInsuranceActivated: isSecondaryInsuranceActivatedField,
      dateOfService: isValidDate(dateOfServiceField)
        ? convertToUTC(dateOfServiceField)
        : null,
      patient: {
        firstName: firstNameField,
        lastName: lastNameField,
        dateOfBirth: isValidDate(dateOfBirthField)
          ? convertToUTC(dateOfBirthField)
          : null,
      },
      orderId: orderId || null,
      excludedNcciRulesetIds,
    });
    setValue("gfeQuoteId", null);
  }, [
    serviceDetailsField,
    deductibleField,
    outOfPocketMaxField,
    flatCopayField,
    coinsurancePercentField,
    facilityCopayField,
    facilityCoinsuranceField,
    facilityDeductibleField,
    professionalFlatCopayField,
    professionalCopayField,
    professionalCoinsuranceField,
    professionalDeductibleField,
    dateOfServiceField,
    firstNameField,
    lastNameField,
    dateOfBirthField,
    deductible2Field,
    outOfPocketMax2Field,
    flatCopay2Field,
    professionalFlatCopay2Field,
    coinsurancePercent2Field,
    facilityCopay2Field,
    facilityCoinsurance2Field,
    facilityDeductible2Field,
    professionalCopay2Field,
    professionalCoinsurance2Field,
    professionalDeductible2Field,
    showProfessionalCopay2Field,
    isSecondaryInsuranceActivatedField,
    excludedNcciRulesetIds,
  ]);

  useEffect(() => {
    if (initialProviderProcedures?.length) {
      setCurrentProviderProcedures(initialProviderProcedures);
    }
  }, [initialProviderProcedures]);

  useEffect(() => {
    buildSelectedProceduresPriceBreakdownToTable({ priceBreakdown, dispatch });

    // Sync excludedNcciInfo from the pricing response (handles draft reload case
    // where the info wasn't persisted but the backend reconstructs it from ruleset IDs)
    if (
      (priceBreakdown as any)?.excludedNcciInfo?.length > 0 &&
      excludedNcciInfo.length === 0
    ) {
      setExcludedNcciInfo((priceBreakdown as any).excludedNcciInfo);
    }

    if (priceBreakdown?.progressiveDeductible) {
      const progressiveDeductible =
        priceBreakdown.progressiveDeductible as IProgressiveDeductible;
      const {
        totalDeductibleForMatchingOrders,
        totalOutOfPocketForMatchingOrders,
        matchingOrders = [],
      } = progressiveDeductible;

      // Calculate the reduction amounts (in dollars)
      const deductibleReductionAmount = totalDeductibleForMatchingOrders / 100;
      const oopReductionAmount = totalOutOfPocketForMatchingOrders / 100;

      // Check if we have amounts to reduce and if reductions have already been applied
      const hasMatchingOrders =
        deductibleReductionAmount > 0 || oopReductionAmount > 0;

      // Update form context with values for PatientBenefitsForm to use
      setValue("hasMatchingOrders", hasMatchingOrders);
      setValue("deductibleReductionAmount", deductibleReductionAmount);
      setValue("oopReductionAmount", oopReductionAmount);

      // Pass the matching orders data to form context
      const matchingOrdersData = matchingOrders.map((order: IMatchingOrder) => {
        // For each order, we'll calculate the deductible and OOP reductions
        let deductibleReductionInCents = 0;
        let oopReductionInCents = 0;

        // Check if the order has the pricing breakdown with procedure details
        if (
          order.gfeQuote &&
          order.gfeQuote.pricingBreakdown &&
          order.gfeQuote.pricingBreakdown.procedureBreakdown
        ) {
          // Sum up the deductible and OOP reductions from each procedure
          order.gfeQuote.pricingBreakdown.procedureBreakdown.forEach((proc) => {
            if (proc.owedByPatient) {
              // Add the deductible amount
              deductibleReductionInCents += proc.owedByPatient.deductible || 0;

              // Calculate OOP as total minus selfPay
              const total = proc.owedByPatient.total || 0;
              const selfPay = proc.owedByPatient.selfPay || 0;
              oopReductionInCents += total - selfPay;
            }
          });
        } else {
          // If we don't have detailed breakdown, use the order's contribution to the total reduction
          const orderContributionRatio =
            order.amountInCents / totalDeductibleForMatchingOrders;
          deductibleReductionInCents = Math.round(
            totalDeductibleForMatchingOrders * orderContributionRatio
          );
          oopReductionInCents = Math.round(
            totalOutOfPocketForMatchingOrders * orderContributionRatio
          );
        }

        return {
          id: order.id,
          dateOfService: order.dateOfService,
          amountInCents: order.amountInCents,
          deductibleReductionInCents,
          oopReductionInCents,
          patientFirstName: order.patient.firstName,
          patientLastName: order.patient.lastName,
          orderStatus: order.status,
          accountId: order.accountId,
        };
      });
      setValue("matchingOrdersData", matchingOrdersData);

      // Calculate recommended values for reference
      const recommendedDeductible = Math.max(
        0,
        (deductibleField || 0) - deductibleReductionAmount
      );
      const recommendedOutOfPocketMax = Math.max(
        0,
        (outOfPocketMaxField || 0) - oopReductionAmount
      );
      setValue("recommendedDeductible", recommendedDeductible);
      setValue("recommendedOutOfPocketMax", recommendedOutOfPocketMax);
    } else {
      // Reset values if no progressive deductible data
      setValue("hasMatchingOrders", false);
      setValue("deductibleReductionAmount", 0);
      setValue("oopReductionAmount", 0);
      setValue("matchingOrdersData", []);
      setValue("recommendedDeductible", null);
      setValue("recommendedOutOfPocketMax", null);
    }
  }, [
    priceBreakdown,
    deductibleField,
    outOfPocketMaxField,
    reductionsAppliedField,
  ]);

  useEffect(() => {
    const totalAmountForGFE = buildGFETotalAmount(
      priceBreakdownRows.patientResponsibility
    );
    setValue("amount", centsToDollars(totalAmountForGFE));
    setValue(
      "totalInsurancePortion",
      centsToDollars(insuranceAmounts.totalInsurancePayInCents)
    );
    setValue(
      "totalInsuranceAllowable",
      centsToDollars(insuranceAmounts.totalAllowedInsuranceAmount)
    );

    // Calculate total amount with Clear Patient Fee and discount
    const totalWithPatientFee =
      centsToDollars(totalAmountForGFE) + clearPatientFee;
    setValue(
      "totalAmount",
      centsToDollars(
        calculateDiscount(dollarsToCents(totalWithPatientFee), discount)
      )
    );

    if (
      redo &&
      totalAmount &&
      centsToDollars(totalAmount) !==
        centsToDollars(
          calculateDiscount(dollarsToCents(totalWithPatientFee), discount)
        )
    ) {
      setShowCustomPriceMessage(true);
    } else {
      setShowCustomPriceMessage(false);
    }
  }, [
    priceBreakdownRows,
    redo,
    totalAmount,
    discount,
    insuranceAmounts,
    clearPatientFee,
  ]);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }
    if (
      !accountId ||
      (redo && redoOrderFirstRenderField) ||
      (draft && draftOrderFirstRenderField) ||
      (copy && copyOrderFirstRenderField) ||
      !autoCompleteValue.length
    )
      return;
    const fetchPayerProviderProcedures = async () => {
      const cptCodesToFetch = buildSelectedProceduresObject([
        ...autoCompleteValue,
      ]).map((item) => item.code);
      setSearchLoading(true);
      const providerProceduresResponses = await Promise.all(
        cptCodesToFetch.map(async (cptCode) => {
          const response = await searchMutation({
            accountId,
            cptCode,
            payerName: payerNameField,
          });
          return (response as any).data.data.data || [];
        })
      );
      setSearchLoading(false);
      const primaryProviderProcedures = providerProceduresResponses.flat();
      const primaryProcedures = primaryProviderProcedures?.map(
        (providerProcedure: any) => {
          return providerProcedure.procedure || [];
        }
      );
      const newAutocompleteSuggestions =
        buildFilteredProceduresSuggestions(primaryProcedures);

      const newAutucompleteValue = [...autoCompleteValue].filter((value) =>
        newAutocompleteSuggestions.includes(value)
      );
      const notFoundCPTCodesValue = [...autoCompleteValue].filter(
        (value) => !newAutocompleteSuggestions.includes(value)
      );
      const notFoundCPTCodesData = buildSelectedProceduresObject([
        ...notFoundCPTCodesValue,
      ])
        .map((item) => item.code)
        .join(", ");
      setNotFoundCPTCodes(notFoundCPTCodesData);
      const newSelectedProceudresObject = buildSelectedProceduresObject([
        ...newAutucompleteValue,
      ]);
      if (notFoundCPTCodesData.length) {
        setShowCPTCodesNotFound(true);
      } else {
        setShowCPTCodesNotFound(false);
      }
      const cptQuantities = getCptQuantities(rows);

      const remappedNewSelectedProcedures = replicateByCptQuantity(
        newSelectedProceudresObject,
        cptQuantities
      );
      const rowsToAdd: ProcedureRow[] = buildSelectedProceduresToTable({
        selectedProceduresObject: remappedNewSelectedProcedures,
        procedures: primaryProcedures,
        providerProcedures: primaryProviderProcedures,
        rows: [],
        row: undefined,
      });
      dispatch(setAutoCompleteValue(newAutucompleteValue));
      dispatch(setSelectedProcedures(newAutucompleteValue));
      dispatch(setRows(rowsToAdd));
    };
    fetchPayerProviderProcedures();
  }, [payerNameField]);

  useEffect(() => {
    if (!autoCompleteValue.length) {
      setCurrentProviderProcedures([]);
    }
  }, [autoCompleteValue]);

  // Fetch CPT recommendations when autoCompleteValue changes
  useEffect(() => {
    if (!accountId || !autoCompleteValue.length) {
      dispatch(clearCptRecommendations());
      return;
    }

    const fetchCptRecommendations = async () => {
      try {
        dispatch(setLoadingCptRecommendations(true));
        const includedCptCodes = buildSelectedProceduresObject(
          autoCompleteValue
        ).map((item) => item.code);

        const response = await getCptRecommendations({
          accountId,
          includedCptCodes,
          payerName: payerNameField || undefined,
        });

        if ("data" in response && response.data?.data?.recommendations) {
          dispatch(setCptRecommendations(response.data.data.recommendations));
        } else {
          dispatch(clearCptRecommendations());
        }
      } catch (error) {
        console.error("Error fetching CPT recommendations:", error);
        dispatch(clearCptRecommendations());
      }
    };

    // Debounce the API call to avoid too many requests
    const timeoutId = setTimeout(fetchCptRecommendations, 500);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [
    accountId,
    autoCompleteValue,
    dispatch,
    getCptRecommendations,
    payerNameField,
  ]);

  useEffect(() => {
    if (!rows?.length && showCPTCodesNotFound) {
      setShowCPTCodesNotFound(false);
    }
  }, [payerNameField, rows]);

  const onAmountChange = useCallback(
    (value: number | null, row: ProcedureRow) => {
      const convertedValue = value ? dollarsToCents(value) : 0;
      const updatedRows = [...rows].map((r) => {
        if (
          r.cptCode === row.cptCode &&
          r.description === row.description &&
          r.specialty === row.specialty &&
          r.providerProcedureId === row.providerProcedureId &&
          JSON.stringify(
            [...r.providerDropdown].sort((a, b) => a.id.localeCompare(b.id))
          ) ===
            JSON.stringify(
              [...row.providerDropdown].sort((a, b) => a.id.localeCompare(b.id))
            ) &&
          JSON.stringify(r.selectedProvider) ===
            JSON.stringify(row.selectedProvider)
        ) {
          return {
            ...r,
            amount: convertedValue,
          };
        }
        return r;
      });
      dispatch(setRows(updatedRows));
    },
    [rows, dispatch]
  );

  const debouncedOnAmountChange = useMemo(
    () => debounce(onAmountChange, 500),
    [onAmountChange]
  );

  const editableAmountTemplate = (row: any) => {
    return (
      <div className="flex gap-2">
        <InputNumber
          currency="USD"
          locale="en-US"
          mode="currency"
          min={0}
          value={centsToDollars(row.amount)}
          onChange={(e) => debouncedOnAmountChange(e.value, row)}
        />
        {row.toBeEdited && (
          <StyledWarning
            severity="warn"
            text="Please insert the correct price before saving"
          />
        )}
      </div>
    );
  };

  const handleRecommendationClick = async (cptCode: string) => {
    try {
      // Set loading state for this specific CPT code
      setLoadingCptCode(cptCode);

      // Search for the CPT code and get the results directly
      const searchResults = await search({ query: cptCode });

      if (!searchResults || searchResults.length === 0) {
        console.warn(`No search results found for CPT code: ${cptCode}`);
        return;
      }

      // Find the procedure in the search results
      const providerProcedure = searchResults.find(
        (pp) => pp.procedure?.cptCode === cptCode
      );
      if (!providerProcedure?.procedure) {
        console.warn(`Procedure not found for CPT code: ${cptCode}`);
        return;
      }

      const { procedure } = providerProcedure;

      // Create the selection value in the format expected by AutoComplete
      const selectionValue = `${cptCode} - ${procedure.description}`;

      // Check if this procedure is already selected
      if (autoCompleteValue.includes(selectionValue)) {
        console.log(`CPT code ${cptCode} is already selected`);
        return;
      }

      // Create a mock AutoCompleteSelectParams event
      const mockEvent = {
        value: selectionValue,
        originalEvent: {
          target: null,
          currentTarget: null,
          type: "select",
          nativeEvent: new Event("select"),
          isDefaultPrevented: () => false,
          isPropagationStopped: () => false,
          persist: () => {},
        } as any,
      };

      // Use the same handler that AutoCompleteCPT uses
      const arrayOfCptCodes = [...selectedProcedures];
      const filteredCpt = arrayOfCptCodes.filter((p) => p === selectionValue);

      // Update current provider procedures if needed
      if (currentProviderProcedures && setCurrentProviderProcedures) {
        const copy = [...currentProviderProcedures];
        const existingIds = new Set(copy.map((pp) => pp.id));
        searchResults.forEach((providerProcedure) => {
          if (!existingIds.has(providerProcedure.id)) {
            copy.push(providerProcedure);
          }
        });
        setCurrentProviderProcedures(copy);
      }

      // Call the proper selection handler with the search results
      await autoCompleteFieldHandleOnSelectProcedure({
        e: mockEvent,
        dispatch,
        selectedProcedures,
        procedures: searchResults.map((pp) => pp.procedure).filter(Boolean),
        providerProcedures: searchResults,
        rows,
        newValue: filteredCpt.length + 1,
      });
    } catch (error) {
      console.error(
        `Error handling recommendation click for CPT ${cptCode}:`,
        error
      );
    } finally {
      // Clear loading state
      setLoadingCptCode(null);
    }
  };

  const handleAddImplant = async (
    cptCode: string,
    suggestedAmount?: number
  ) => {
    try {
      setLoadingCptCode(cptCode);

      const searchResults = await search({ query: cptCode });

      if (!searchResults || searchResults.length === 0) {
        toastElement?.show({
          severity: "warn",
          summary: "Implant Not Found",
          detail: `Implant code ${cptCode} is not set up for this facility. Please add it to the fee schedule first.`,
          life: 5000,
        });
        return;
      }

      const providerProcedure = searchResults.find(
        (pp) => pp.procedure?.cptCode === cptCode
      );
      if (!providerProcedure?.procedure) {
        toastElement?.show({
          severity: "warn",
          summary: "Implant Not Found",
          detail: `Implant code ${cptCode} is not set up for this facility. Please add it to the fee schedule first.`,
          life: 5000,
        });
        return;
      }

      const { procedure } = providerProcedure;
      const selectionValue = `${cptCode} - ${procedure.description}`;

      if (autoCompleteValue.includes(selectionValue)) {
        toastElement?.show({
          severity: "info",
          summary: "Already Added",
          detail: `Implant code ${cptCode} is already in the order.`,
          life: 3000,
        });
        return;
      }

      const mockEvent = {
        value: selectionValue,
        originalEvent: {
          target: null,
          currentTarget: null,
          type: "select",
          nativeEvent: new Event("select"),
          isDefaultPrevented: () => false,
          isPropagationStopped: () => false,
          persist: () => {},
        } as any,
      };

      const arrayOfCptCodes = [...selectedProcedures];
      const filteredCpt = arrayOfCptCodes.filter((p) => p === selectionValue);

      if (currentProviderProcedures && setCurrentProviderProcedures) {
        const copy = [...currentProviderProcedures];
        const existingIds = new Set(copy.map((pp) => pp.id));
        searchResults.forEach((pp) => {
          if (!existingIds.has(pp.id)) {
            copy.push(pp);
          }
        });
        setCurrentProviderProcedures(copy);
      }

      await autoCompleteFieldHandleOnSelectProcedure({
        e: mockEvent,
        dispatch,
        selectedProcedures,
        procedures: searchResults.map((pp) => pp.procedure).filter(Boolean),
        providerProcedures: searchResults,
        rows,
        newValue: filteredCpt.length + 1,
      });

      // If a suggested amount was provided, update the newly added row's amount
      if (suggestedAmount !== undefined) {
        // Small delay to ensure the row has been added to Redux
        setTimeout(() => {
          // Get the current rows from Redux and update the implant's amount
          const updatedRows = rows
            .concat(
              buildSelectedProceduresToTable({
                selectedProceduresObject: buildSelectedProceduresObject([
                  selectionValue,
                ]),
                procedures: searchResults
                  .map((pp) => pp.procedure)
                  .filter(Boolean),
                providerProcedures: searchResults,
                rows: [],
                row: undefined,
              })
            )
            .map((r) => {
              if (r.cptCode === cptCode) {
                return { ...r, amount: suggestedAmount };
              }
              return r;
            });
          dispatch(setRows(updatedRows));
        }, 100);
      }

      toastElement?.show({
        severity: "success",
        summary: "Implant Added",
        detail: `Added ${cptCode} to the order.`,
        life: 3000,
      });
    } catch (error) {
      console.error(`Error adding implant ${cptCode}:`, error);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to add implant code ${cptCode}.`,
        life: 5000,
      });
    } finally {
      setLoadingCptCode(null);
    }
  };

  return (
    <>
      <StyledCard title="Service" data-testid="serviceDetails">
        <div className="formgrid grid w-100">
          <div className="field p-fluid col-12">
            <AutoCompleteCPT
              search={(e) => {
                setShowCPTCodesNotFound(false);
                setNotFoundCPTCodes("");
                search(e);
              }}
              disabled={searchLoading}
              setCurrentProviderProcedures={setCurrentProviderProcedures}
              autoCompleteValue={autoCompleteValue}
              procedures={procedures}
              providerProcedures={providerProcedures}
              currentProviderProcedures={currentProviderProcedures}
              onFocus={() => setIsCptInputFocused(true)}
              onBlur={() => setIsCptInputFocused(false)}
            />
            <CptRecommendations
              onRecommendationClick={handleRecommendationClick}
              loadingCptCode={loadingCptCode}
            />
            {accountData?.data?.features?.implantSuggestions && (
              <ImplantSuggestion
                cptCodes={buildSelectedProceduresObject(autoCompleteValue).map(
                  (item) => item.code
                )}
                cptQuantities={getCptQuantities(rows)}
                payerId={payerField?.payerId}
                payerName={payerNameField}
                accountId={accountId}
                onAddImplant={handleAddImplant}
                isLoading={searchLoading}
                disabled={searchLoading}
                isCptInputFocused={isCptInputFocused}
              />
            )}
            {showCPTCodesNotFound && !searchLoading && (
              <p className="mt-1 text-orange-500 text-sm">
                CPT Codes Not Found for this payer - {notFoundCPTCodes}
              </p>
            )}
            {searchLoading && (
              <LoadingSpinner
                customStyle={{
                  width: "30px",
                  height: "30px",
                }}
              />
            )}
          </div>
        </div>

        <DataTable
          stripedRows
          value={dataTableRows}
          groupRowsBy="cptCode"
          rowGroupMode="rowspan"
          responsiveLayout="scroll"
          emptyMessage="No CPT codes selected."
          style={{
            marginLeft: sizer(-4),
            marginRight: sizer(-4),
          }}
        >
          <Column
            field="cptCode"
            header="CPT Code/Name"
            body={(row) =>
              buildFirstColumnWithQuantityTemplate({
                row,
                rows,
                autoCompleteValue,
                dispatch,
                selectedProcedures,
                procedures,
                providerProcedures,
                isGFE: true,
              })
            }
            style={{
              minWidth: "410px",
              maxWidth: "410px",
              paddingLeft: sizer(6),
            }}
          />
          <Column
            field="amount"
            header="Amount"
            body={(row) => editableAmountTemplate(row)}
            headerStyle={{ width: "250px" }}
          />
          <Column
            field="specialty"
            header="Specialty"
            body={(row) => SpecialtyListDict[row.specialty as SpecialtyList]}
          />
          <Column
            field="provider"
            header="Provider"
            body={(row) => providerDropdownTemplate(row)}
          />
          <Column
            style={{ maxWidth: "80px" }}
            body={(row) => actionTemplate(row)}
          />
        </DataTable>
        {(priceBreakdownRows.adjustments.length > 0 ||
          excludedNcciInfo.length > 0) && (
          <AdjustmentDetails
            displayAdjustments={displayAdjustments}
            priceBreakdownRows={priceBreakdownRows}
            dispatch={dispatch}
            priceExecuting={priceExecuting}
            excludedNcciInfo={excludedNcciInfo}
            onNcciToggle={(
              rulesetId: string,
              info: { cptCode: string; discountedCptCode: string }
            ) => {
              if (excludedNcciRulesetIds.includes(rulesetId)) {
                setExcludedNcciRulesetIds((prev) =>
                  prev.filter((id) => id !== rulesetId)
                );
                setExcludedNcciInfo((prev) =>
                  prev.filter((item) => item.rulesetId !== rulesetId)
                );
              } else {
                setExcludedNcciRulesetIds((prev) => [...prev, rulesetId]);
                setExcludedNcciInfo((prev) => [
                  ...prev,
                  { rulesetId, ...info },
                ]);
              }
            }}
          />
        )}
      </StyledCard>

      <PatientResponsibilityDetails
        displayPatientResponsibility={displayPatientResponsibility}
        priceBreakdownRows={priceBreakdownRows}
        dispatch={dispatch}
        priceExecuting={priceExecuting}
      />

      {!priceExecuting && !isDiscountLoading ? (
        <div className="mb-3">
          <GFEFieldsContainer>
            <div className="flex">
              <label
                htmlFor="totalInsuranceAllowable"
                className="mr-2 flex align-items-center"
              >
                Total Insurance Allowable
              </label>

              <Controller
                name="totalInsuranceAllowable"
                control={control}
                render={({ field: { value } }) => (
                  <InputNumber
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    inputId="totalInsuranceAllowable"
                    disabled
                  />
                )}
              />
            </div>
          </GFEFieldsContainer>
          {showMore && (
            <>
              <GFEFieldsContainer>
                <div className="flex">
                  <label
                    htmlFor="totalInsurancePortion"
                    className="mr-2 flex align-items-center"
                  >
                    Total Insurance Portion
                  </label>

                  <Controller
                    name="totalInsurancePortion"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <InputNumber
                        value={value}
                        currency="USD"
                        locale="en-US"
                        mode="currency"
                        inputId="totalInsurancePortion"
                        onValueChange={({ value }) => onChange(value)}
                        min={0}
                        disabled
                      />
                    )}
                  />
                </div>
              </GFEFieldsContainer>
              <GFEFieldsContainer>
                <div className="flex">
                  <label
                    htmlFor="amount"
                    className="mr-2 flex align-items-center"
                  >
                    {clearPatientFee > 0
                      ? "Subtotal Patient Estimate"
                      : "Total Patient Estimate"}
                  </label>

                  <Controller
                    name="amount"
                    control={control}
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputNumber
                          value={value}
                          currency="USD"
                          locale="en-US"
                          mode="currency"
                          inputId="amount"
                          disabled
                          onValueChange={({ value }) => onChange(value)}
                          minFractionDigits={2}
                          maxFractionDigits={5}
                          min={0}
                          onBlur={() => {
                            handleOnBlurField({
                              onBlur,
                              field: "amount",
                              trigger,
                            });
                          }}
                        />
                        {getFormErrorMessage("amount", errors)}
                      </>
                    )}
                  />
                </div>
              </GFEFieldsContainer>
              {clearPatientFee > 0 && (
                <GFEFieldsContainer>
                  <div className="flex">
                    <label className="mr-2 flex align-items-center">
                      Clear Patient Fee ({clearPatientFeePercentage}
                      %)
                    </label>
                    <InputNumber
                      value={clearPatientFee}
                      currency="USD"
                      locale="en-US"
                      mode="currency"
                      disabled
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      min={0}
                    />
                  </div>
                </GFEFieldsContainer>
              )}
              {clearPatientFee > 0 && (
                <GFEFieldsContainer>
                  <div className="flex">
                    <label className="mr-2 flex align-items-center">
                      Total Patient Estimate
                    </label>
                    <InputNumber
                      value={totalAmountWithPatientFee}
                      currency="USD"
                      locale="en-US"
                      mode="currency"
                      disabled
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      min={0}
                    />
                  </div>
                </GFEFieldsContainer>
              )}
            </>
          )}
          <GFEFieldsContainer>
            <div className="flex">
              <label
                htmlFor="totalAmount"
                className="mr-2 flex align-items-center"
              >
                Clear Price for Patient
              </label>

              <Controller
                name="totalAmount"
                control={control}
                rules={{
                  required: validationConstants.SERVICE_DETAILS.GFE_CLEAR_PRICE,
                }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <>
                    <InputNumber
                      value={value}
                      currency="USD"
                      locale="en-US"
                      mode="currency"
                      inputId="totalAmount"
                      onValueChange={({ value }) => {
                        onChange(value);
                        setShowCustomPriceMessage(false);
                      }}
                      disabled
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      min={0}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "amount",
                          trigger,
                        });
                      }}
                    />
                    {getFormErrorMessage("totalAmount", errors)}
                  </>
                )}
              />
            </div>
          </GFEFieldsContainer>
          {showCustomPriceMessage && totalAmount && (
            <GFEFieldsContainer>
              <label className="text-orange-400">
                Clear Price for Patient for this order was set to $
                {centsToDollars(totalAmount)}
              </label>
            </GFEFieldsContainer>
          )}
          <GFEFieldsContainer>
            <Button
              type="button"
              className="p-button-link"
              icon={showMore ? "pi pi-chevron-up" : "pi pi-chevron-down"}
              onClick={() => setShowMore((prev) => !prev)}
              label={showMore ? "Show Less" : "Show More"}
            />
          </GFEFieldsContainer>
        </div>
      ) : (
        <LoadingContainer>
          <LoadingSpinner customStyle={{ width: "60px", height: "60px" }} />
          <h3>Calculating Totals</h3>
        </LoadingContainer>
      )}
    </>
  );
}
